import { useState, useEffect, useCallback } from 'react';
import { API_TIMEOUT, buildApiUrl } from '../config';

const parseDate = (dateString) => {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;
  return new Date(dateString);
};

const formatDateForAPI = (dateString, type = 'start') => {
  if (!dateString) return '';
  const date = parseDate(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const time = type === 'end' ? '23:59:59' : '06:00:00';
  
  return `${year}-${month}-${day} ${time}`;
};

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data,
      error: data.error,
      mensaje: data.mensaje
    };
    
  } catch (error) {
    throw error;
  }
};

export const useCupones = (options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    immediate = false
  } = options;

  const fetchCupones = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(buildApiUrl('cupones'), {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const validatedData = result.data || result;
      setData(validatedData);
      return validatedData;
    } catch (err) {
      const errorMessage = err.name === 'AbortError' 
        ? 'Tiempo de espera agotado' 
        : 'Error de conexión con el servidor';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchCupones();
    }
  }, [immediate, fetchCupones]);

  return {
    data,
    loading,
    error,
    refetch: fetchCupones,
  };
};

export const getCuponById = async (id) => {
  return apiRequest(`cupon/${id}`, { method: 'GET' });
};

export const getCatalogoTarjetas = async () => {
  return apiRequest('catalogo-tarjetas', { method: 'GET' });
};

export const createCupon = async (cuponData) => {
  const dataToSend = {
    PorcentajeDescuento: cuponData.PorcentajeDescuento,
    Empresa: cuponData.Empresa,
    Fecha_fin: formatDateForAPI(cuponData.Fecha_fin, 'end'),
    Descripcion: cuponData.Descripcion,
    CodigoPromo: cuponData.CodigoPromo,
    IdTipoTarjeta: cuponData.IdTipoTarjeta
  };
  return apiRequest('cupon', {
    method: 'POST',
    body: JSON.stringify(dataToSend)
  });
};

export const updateCupon = async (id, cuponData) => {
  const dataToSend = {
    PorcentajeDescuento: cuponData.PorcentajeDescuento,
    Empresa: cuponData.Empresa,
    Fecha_fin: formatDateForAPI(cuponData.Fecha_fin, 'end'),
    Descripcion: cuponData.Descripcion,
    CodigoPromo: cuponData.CodigoPromo,
    IdTipoTarjeta: cuponData.IdTipoTarjeta
  };
  return apiRequest(`cupon/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dataToSend)
  });
};

export const deleteCupon = async (id) => {
  return apiRequest(`cupon/${id}`, {
    method: 'DELETE'
  });
};

export const validarCupon = async (codigoPromo) => {
  return apiRequest('cupones/validar', {
    method: 'POST',
    body: JSON.stringify({ pCodigoPromo: codigoPromo })
  });
};

export default {
  useCupones,
  getCuponById,
  getCatalogoTarjetas,
  createCupon,
  updateCupon,
  deleteCupon,
  validarCupon
};
